import { useEffect, useMemo, useRef, useState } from "react";
import { money, thumb, TypeChip } from "./shared";

/* Build-your-own-bundle, modeled on the Simple Bundles storefront widget:
   product picker grid (left, 6fr) + bundle side cart (right, 4fr) with
   pack chips, picked-item rows, dashed placeholder slots, subtotal with
   discount badge, and a CTA disabled until the bundle is full. On mobile
   the cart becomes a fixed bottom drawer. */
export default function ByobGrid({ bundle }) {
  const tiers = bundle.metadata?.tiers?.length
    ? bundle.metadata.tiers
    : [{ label: `${bundle.item_count}-Pack`, item_count: bundle.item_count, discount_percentage: bundle.discount_percentage }];

  const [tierIndex, setTierIndex] = useState(0);
  const [rows, setRows] = useState([]); // [{ rowId, item, variant }] — one row per unit, like the app
  const [modalItem, setModalItem] = useState(null); // product awaiting variant selection
  const [cartState, setCartState] = useState("idle");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartHeight, setCartHeight] = useState(280);
  const [sectionInView, setSectionInView] = useState(true);
  const nextRowId = useRef(1);
  const listRef = useRef(null);
  const articleRef = useRef(null);
  const cartRef = useRef(null);

  // Mobile: the cart is a fixed bottom drawer. Keep a spacer exactly as
  // tall as the cart so the last grid rows stay reachable...
  useEffect(() => {
    const cart = cartRef.current;
    if (!cart || !("ResizeObserver" in window)) return;
    const observer = new ResizeObserver(() => setCartHeight(cart.offsetHeight));
    observer.observe(cart);
    return () => observer.disconnect();
  }, []);

  // ...and only pin it while the BYOB section is actually on screen;
  // it slides away once the visitor scrolls past.
  useEffect(() => {
    const article = articleRef.current;
    if (!article || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => setSectionInView(entry.isIntersecting),
      { rootMargin: "-72px 0px -56px 0px" }
    );
    observer.observe(article);
    return () => observer.disconnect();
  }, []);

  const tier = tiers[tierIndex];
  const target = tier.item_count;
  const full = rows.length >= target;
  const remaining = target - rows.length;

  const sections = useMemo(() => {
    const grouped = new Map();
    bundle.items.forEach((item) => {
      const key = item.group_name || "Pick your pieces";
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(item);
    });
    return [...grouped.entries()];
  }, [bundle]);

  const subtotal = rows.reduce((sum, row) => sum + row.variant.price, 0);
  const discounted = subtotal * (1 - tier.discount_percentage / 100);

  const defaultVariantFor = (item) => ({
    id: item.variant_id,
    title: item.variant_title,
    price: item.price,
  });

  const addItem = (item, variant = null, quantity = 1) => {
    const chosen = variant || defaultVariantFor(item);
    setRows((current) => {
      const space = target - current.length;
      const count = Math.min(quantity, space);
      if (count <= 0) return current;
      const added = Array.from({ length: count }, () => ({
        rowId: nextRowId.current++,
        item,
        variant: chosen,
      }));
      return [...current, ...added];
    });
    requestAnimationFrame(() => {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    });
  };

  const removeRow = (rowId) => {
    setRows((current) => {
      const next = current.filter((row) => row.rowId !== rowId);
      if (next.length === 0) setDrawerOpen(false);
      return next;
    });
  };

  const changeTier = (index) => {
    setTierIndex(index);
    setRows((current) => current.slice(0, tiers[index].item_count));
  };

  const addBundleToCart = () => {
    if (!full || cartState !== "idle") return;
    setCartState("added");
    setTimeout(() => setCartState("idle"), 2000);
  };

  return (
    <article ref={articleRef} className="card p-5 lg:p-8">
      {/* fixed cart column so long product titles can't reshape the layout */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        {/* ---- Product picker ---- */}
        <div>
          {sections.map(([sectionName, items]) => (
            <div key={sectionName} className="mb-8 last:mb-0">
              {sections.length > 1 && <h4 className="mb-3 font-display text-lg font-semibold">{sectionName}</h4>}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {items.map((item) => (
                  <PickerCard
                    key={item.id}
                    item={item}
                    full={full}
                    onAdd={() => addItem(item)}
                    onSelect={() => setModalItem(item)}
                  />
                ))}
              </div>
            </div>
          ))}
          {/* spacer sized to the fixed mobile cart so the last rows stay reachable */}
          <div className="lg:hidden" style={{ height: cartHeight + 24 }} aria-hidden="true"></div>
        </div>

        {/* ---- Side cart ---- */}
        <aside
          ref={cartRef}
          aria-label="Bundle summary"
          className={`border-2 border-ink bg-white lg:sticky lg:top-24 lg:rounded-lg lg:p-7 max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-30 max-lg:max-h-[85dvh] max-lg:overflow-auto max-lg:border-x-0 max-lg:border-b-0 max-lg:p-4 max-lg:shadow-[0_-4px_20px_rgba(0,0,0,0.12)] max-lg:transition-transform max-lg:duration-300 ${
            sectionInView ? "" : "max-lg:pointer-events-none max-lg:translate-y-full"
          }`}
        >
          {/* mobile drawer toggle */}
          <div className="mb-2 flex items-center justify-between lg:hidden">
            <h3 className="font-display text-lg font-semibold">Your bundle</h3>
            {rows.length > 0 && (
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-semibold text-ink-soft"
                onClick={() => setDrawerOpen((open) => !open)}
                aria-expanded={drawerOpen}
              >
                {drawerOpen ? "Close" : "View items"}
                <svg className={`h-4 w-4 transition-transform ${drawerOpen ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
                  <path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>

          <h3 className="hidden font-display text-2xl font-semibold leading-tight lg:block">{bundle.title}</h3>
          <p className="mt-1.5 hidden text-[13px] leading-5 text-ink-soft lg:block">{bundle.description}</p>

          {tiers.length > 1 && (
            <nav aria-label="Pack options" className="mt-4 flex flex-wrap gap-2">
              {tiers.map((option, index) => (
                <button
                  key={option.label}
                  type="button"
                  aria-current={index === tierIndex ? "page" : undefined}
                  onClick={() => changeTier(index)}
                  className={`min-h-7 rounded-md border-[1.5px] px-3 py-1 text-xs font-semibold transition ${
                    index === tierIndex ? "border-ink bg-ink text-white" : "border-ink text-ink hover:bg-ink/5"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </nav>
          )}

          <hr className={`my-4 border-t-[1.5px] border-ink/15 ${drawerOpen ? "" : "max-lg:hidden"}`} />

          {/* picked items + placeholder slots */}
          <div
            ref={listRef}
            className={`flex max-h-[432px] flex-col gap-3 overflow-auto ${drawerOpen ? "" : "max-lg:hidden"}`}
          >
            {rows.map(({ rowId, item, variant }) => (
              <article key={rowId} className="flex items-center gap-3" aria-label={item.title}>
                <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border-[1.5px] border-ink/15 bg-white">
                  <img src={thumb(item.image_url, 160)} alt="" aria-hidden="true" className="byob-thumb-blur absolute inset-0 h-full w-full object-cover" />
                  <img src={thumb(item.image_url, 160)} alt="" loading="lazy" className="relative h-full w-full object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{item.title}</div>
                  {variant.title && <div className="truncate text-xs text-ink-soft">{variant.title}</div>}
                  <div className="text-sm text-ink-soft">
                    {full && bundle.metadata?.tiers ? (
                      <>
                        <del className="text-ink/40">{money(variant.price)}</del>{" "}
                        <ins className="no-underline" style={{ color: "#b71c1c" }}>
                          {money(variant.price * (1 - tier.discount_percentage / 100))}
                        </ins>
                      </>
                    ) : (
                      money(variant.price)
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(rowId)}
                  className="flex items-center gap-1 text-xs font-semibold text-ink-soft transition hover:text-ink"
                  aria-label={`Remove ${item.title}`}
                >
                  <span className="underline underline-offset-2">Remove</span>
                  <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              </article>
            ))}

            {Array.from({ length: Math.max(0, remaining) }, (_, index) => (
              <div key={`placeholder-${index}`} className="flex items-center gap-3" aria-hidden="true">
                <div className="byob-placeholder-thumb flex h-20 w-20 shrink-0 items-center justify-center rounded-lg">
                  <svg className="h-5 w-5 text-ink/30" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium text-ink-soft">Add a product</div>
                  <div className="text-xs text-ink/40">{tier.label}</div>
                </div>
              </div>
            ))}
          </div>

          <hr className="my-4 border-t-[1.5px] border-ink/15" />

          {!full && tier.discount_percentage > 0 && rows.length > 0 && (
            <p className="mb-3 rounded-md bg-[#f0f0f0] px-3 py-2 text-sm text-[#616161]">
              Add {remaining} more to get {Math.round(tier.discount_percentage)}% off
            </p>
          )}

          <div className="flex items-center justify-between" aria-label="Subtotal">
            <span className="text-sm">Subtotal</span>
            <div className="flex items-baseline gap-2">
              {full && subtotal > 0 ? (
                <>
                  <del className="text-sm text-ink/40">{money(subtotal)}</del>
                  <ins className="text-base font-semibold no-underline" style={{ color: "#b71c1c" }}>{money(discounted)}</ins>
                  <span className="rounded-md bg-[rgba(183,28,28,0.1)] px-2 py-0.5 text-xs font-semibold text-[#303030]">
                    -{Math.round(tier.discount_percentage)}%
                  </span>
                </>
              ) : (
                <span className="text-base font-semibold">{money(subtotal)}</span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={addBundleToCart}
            disabled={!full}
            className={`mt-4 flex min-h-11 w-full items-center justify-center gap-1.5 rounded-md text-sm font-semibold transition ${
              cartState === "added"
                ? "bg-sky-deep text-white"
                : full
                  ? "bg-ink text-white hover:bg-[#23272d]"
                  : "cursor-not-allowed bg-ink/10 text-ink/40"
            }`}
          >
            {cartState === "added" ? (
              <>
                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Added to cart
              </>
            ) : (
              <>
                <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                {full ? "Add bundle to cart" : `Add ${remaining} more`}
              </>
            )}
          </button>
          {cartState === "added" && (
            <p className="mt-2 text-center text-xs text-ink-soft animate-pop">
              This is a preview — on your store, this just worked for real.
            </p>
          )}

          <p className="mt-4 text-center text-xs text-ink-soft">
            Powered by{" "}
            <a href="https://www.simplebundles.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-ink">
              Simple Bundles
            </a>
          </p>
        </aside>
      </div>

      {modalItem && (
        <OptionsModal
          item={modalItem}
          remaining={Math.max(1, remaining)}
          onClose={() => setModalItem(null)}
          onAdd={(variant, quantity) => {
            addItem(modalItem, variant, quantity);
            setModalItem(null);
          }}
        />
      )}
    </article>
  );
}

/* The app's "options modal": pick size/variant (and quantity) before a
   multi-variant product joins the bundle. */
function OptionsModal({ item, remaining, onClose, onAdd }) {
  const axes = useMemo(() => {
    const names = item.option_names?.length ? item.option_names : ["Option"];
    return names
      .map((name, index) => {
        const key = `option${index + 1}`;
        const values = [...new Set(item.variants.map((v) => v[key]).filter(Boolean))];
        return { name, key, values };
      })
      .filter((axis) => axis.values.length > 0);
  }, [item]);

  const [choice, setChoice] = useState(() =>
    Object.fromEntries(axes.map((axis) => [axis.key, item.variants[0]?.[axis.key]]))
  );
  const [quantity, setQuantity] = useState(1);

  const variant = item.variants.find((v) => axes.every((axis) => v[axis.key] === choice[axis.key]));

  useEffect(() => {
    const escape = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-float animate-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-4">
          <img src={thumb(variant?.image_url || item.image_url, 240)} alt="" className="h-24 w-24 shrink-0 rounded-xl border border-ink/10 object-cover" />
          <div className="min-w-0 flex-1">
            <h4 className="font-display text-lg font-semibold leading-snug">{item.title}</h4>
            <p className="mt-0.5 text-sm text-ink-soft">{money(variant ? variant.price : item.price)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 text-ink-soft transition hover:bg-ink/5 hover:text-ink">
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="mt-5 space-y-3.5">
          {axes.map((axis) => (
            <label key={axis.key} className="block">
              <span className="mb-1.5 block text-sm font-semibold">{axis.name}</span>
              <div className="relative">
                <select
                  className="rich-select w-full rounded-xl border border-ink/15 bg-white px-3.5 py-2.5 pr-9 text-sm outline-none transition focus:border-sky-deep"
                  value={choice[axis.key] || ""}
                  onChange={(e) => setChoice((c) => ({ ...c, [axis.key]: e.target.value }))}
                >
                  {axis.values.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </label>
          ))}

          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Quantity</span>
            <div className="flex items-center gap-1 rounded-full border border-ink/15">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg transition hover:bg-ink/5 disabled:opacity-30"
                aria-label="Decrease quantity"
              >−</button>
              <span className="w-6 text-center text-sm font-semibold">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(remaining, q + 1))}
                disabled={quantity >= remaining}
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg transition hover:bg-ink/5 disabled:opacity-30"
                aria-label="Increase quantity"
              >+</button>
            </div>
          </div>
        </div>

        <button
          type="button"
          disabled={!variant}
          onClick={() => variant && onAdd({ id: variant.id, title: variant.title, price: variant.price }, quantity)}
          className={`mt-5 flex min-h-11 w-full items-center justify-center gap-1.5 rounded-md text-sm font-semibold transition ${
            variant ? "bg-ink text-white hover:bg-[#23272d]" : "cursor-not-allowed bg-ink/10 text-ink/40"
          }`}
        >
          {variant ? `Add ${quantity > 1 ? `${quantity} ` : ""}to bundle` : "Unavailable"}
        </button>
      </div>
    </div>
  );
}

/* Product card in the picker grid. Single-variant products add in one
   click (with the app's "Added" flash); multi-variant products open the
   options modal via "Select". */
function PickerCard({ item, full, onAdd, onSelect }) {
  const [flash, setFlash] = useState(false);
  const hasVariants = (item.variants?.length || 0) > 1;

  const click = () => {
    if (full) return;
    if (hasVariants) {
      onSelect();
      return;
    }
    onAdd();
    setFlash(true);
    setTimeout(() => setFlash(false), 1200);
  };

  return (
    <div className="rounded-xl border border-ink/10 bg-white p-2.5">
      <img src={thumb(item.image_url, 400)} alt={item.title} loading="lazy" className="aspect-square w-full rounded-lg object-cover" />
      <div className="mt-2 truncate text-[13px] font-medium">{item.title}</div>
      <div className="text-xs text-ink-soft">{money(item.price)}</div>
      <button
        type="button"
        onClick={click}
        disabled={full && !flash}
        className={`mt-2 flex min-h-8 w-full items-center justify-center gap-1 rounded-md border-[1.5px] text-xs font-semibold transition ${
          flash
            ? "border-ink bg-ink text-white"
            : full
              ? "cursor-not-allowed border-ink/15 text-ink/35"
              : "border-ink text-ink hover:bg-ink hover:text-white"
        }`}
      >
        {flash ? (
          <>
            <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Added
          </>
        ) : full ? (
          "Max quantity added"
        ) : hasVariants ? (
          "Select"
        ) : (
          <>
            <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            Add
          </>
        )}
      </button>
    </div>
  );
}
